const GYM_ADMIN_ROLES = new Set([
  'admin_marca', 'admin_gimnasio', 'admin_gym', 'admin_gym_virtual', 'admin_sede', 'admin_d28d',
]);
const COACH_ADMIN_ROLES = new Set(['entrenador', 'admin_entrenador', 'admin_training', 'entrenador_d28d', 'entrenador_gym']);

export function findOrgOperator(users, { gymId = null, trainerId = null } = {}) {
  if (!Array.isArray(users)) return null;
  return users.find((u) => {
    const roles = [u.rol, ...(Array.isArray(u.roles) ? u.roles : [])].filter(Boolean);
    if (gymId != null && Number(u.gym_id || u.gymId) === Number(gymId)) {
      return roles.some((r) => GYM_ADMIN_ROLES.has(r));
    }
    if (trainerId != null && Number(u.trainer_id) === Number(trainerId)) {
      return roles.some((r) => COACH_ADMIN_ROLES.has(r));
    }
    return false;
  }) || null;
}

export function formatPlanLabel(entity, plansByName = {}) {
  if (!entity) return '—';
  const ids = Array.isArray(entity.available_plan_ids) ? entity.available_plan_ids : [];
  if (ids.length) {
    return ids.map((n) => plansByName[n]?.nombre || n).join(', ');
  }
  if (entity.plan_id) return plansByName[entity.plan_id]?.nombre || entity.plan_id;
  return '—';
}

export function summarizeSubscription(sub) {
  if (!sub?.has_account) {
    return {
      plan: sub?.plan || '—',
      estado: 'sin cuenta',
      daysLeft: null,
      validUntil: null,
      validFrom: null,
      active: false,
      accountId: null,
      pending: false,
    };
  }
  return {
    plan: sub.plan || '—',
    estado: sub.estado || '—',
    daysLeft: sub.days_left,
    validUntil: sub.fecha_vencimiento,
    validFrom: sub.fecha_inicio || null,
    active: Boolean(sub.active),
    accountId: sub.account_id,
    pending: Boolean(sub.pending),
  };
}

/** Operadores (coach, admin gym) con licencias de módulo pero sin cuenta comercial. */
function operatorHasOrgLicenses(operator) {
  if (!operator) return false;
  const rows = Array.isArray(operator.licenses) ? operator.licenses : [];
  if (rows.some((lic) => lic.active !== false)) return true;
  const ma = operator.module_access;
  if (!ma || typeof ma !== 'object') return false;
  return Object.entries(ma).some(([key, val]) => {
    if (!val || val === false) return false;
    return String(key).toLowerCase() !== 'admin';
  });
}

/** Suscripción + estado operativo cuando no hay cuenta de facturación. */
export function summarizeOperatorSubscription(operator) {
  const sub = summarizeSubscription(operator?.subscription);
  if (sub.estado !== 'sin cuenta') return sub;
  if (!operatorHasOrgLicenses(operator)) return sub;
  return {
    ...sub,
    estado: 'activo (operador)',
    active: true,
    operatorLicensed: true,
  };
}

export function formatDateLabel(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatDaysRemaining(daysLeft) {
  if (daysLeft == null) return '—';
  if (daysLeft < 0) return 'Vencida';
  if (daysLeft === 0) return 'Hoy';
  return `${daysLeft} d`;
}

export function formatVigenciaLabel(summary) {
  if (!summary?.validUntil) return '—';
  const d = new Date(summary.validUntil);
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  if (summary.daysLeft == null) return date;
  if (summary.daysLeft < 0) return `${date} (vencida)`;
  return `${date} (${summary.daysLeft} d)`;
}

const MODULE_LABELS = {
  d28d: 'D28D',
  gym: 'D28D',
  live_classes: 'D28D',
  food: 'FOOD',
  training: 'Training',
  admin: 'Admin',
};

export function moduleLabel(code) {
  const key = String(code || '').toLowerCase();
  return MODULE_LABELS[key] || key.toUpperCase() || '—';
}

/** FOOD PLAN — módulo en producción; solo lectura desde Organizaciones. */
export function isProtectedFoodModule(code) {
  return String(code || '').toLowerCase() === 'food';
}

export function isOrgLicenseEditable(code) {
  return !isProtectedFoodModule(code);
}

function isoDateInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/** Filas de licencia por organización — 1 org, N licencias (sin duplicar por producto). */
export function buildOrgLicenseRows(licenses = [], subscription = null) {
  const seen = new Set();
  const rows = [];

  if (subscription?.has_account) {
    const sub = summarizeSubscription(subscription);
    rows.push({
      id: 'subscription',
      moduleCode: 'admin',
      label: moduleLabel('admin'),
      plan: sub.plan,
      validFrom: sub.validFrom,
      validUntil: sub.validUntil,
      daysLeft: sub.daysLeft,
      estado: sub.estado,
      active: sub.active,
      accountId: subscription.account_id,
      editable: true,
    });
    seen.add('admin');
  }

  for (const lic of licenses) {
    if (lic.active === false) continue;
    const code = String(lic.module_code || '').toLowerCase();
    const label = moduleLabel(code);
    if (!code || seen.has(label)) continue;
    seen.add(label);
    const until = lic.valid_until ? new Date(lic.valid_until) : null;
    const daysLeft = until ? Math.ceil((until.getTime() - Date.now()) / 86400000) : null;
    rows.push({
      id: `lic-${code}`,
      moduleCode: code,
      label,
      plan: lic.plan || lic.metadata?.plan || '—',
      validFrom: lic.valid_from || null,
      validUntil: lic.valid_until || null,
      daysLeft,
      estado: lic.active === false ? 'inactivo' : 'activo',
      active: lic.active !== false,
      accountId: null,
      editable: false,
    });
  }

  return rows;
}

export { isoDateInputValue };
